/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import Axis from './axis';
import { BLANKSTRING, pluckNumber, setLineHeight, getDashStyle, convertColor,
  pluck, preDefStr, PXSTRING } from '../lib';
import { polarToCartesian } from '@fusioncharts/utils/src/scale-utils/polar-util';
import diff from '@fusioncharts/utils/src/scale-utils/array/diff';
import range from '@fusioncharts/utils/src/scale-utils/array/range';
import polarAnimation from './polar.animation';
import { addDep } from '../dependency-manager';
import ScaleCategory from '@fusioncharts/utils/src/scales/category';
import { setTickValues, shiftLabels,
  _createContainer, setAxisPadding, getPixel, getLabel } from './common-api';

var UNDEF,
  DASH_DEF = 'none',
  // PXSTRING = 'px',
  POINTER = 'pointer',
  NORMALSTRING = 'normal',
  EVENTARGS = 'eventArgs',
  POSITION_MIDDLE = preDefStr.POSITION_MIDDLE,
  POSITION_START = preDefStr.POSITION_START,
  POSITION_TOP = preDefStr.POSITION_TOP,
  POSITION_BOTTOM = preDefStr.POSITION_BOTTOM,
  POSITION_END = preDefStr.POSITION_END,
  // M = 'M',
  math = Math,
  mathMin = math.min,
  pi = math.PI,
  pi2 = 2 * pi;
  // pInt = function (s, mag) {
  //   return parseInt(s, mag || 10);
  // },
  // labelFontBdrColor,

addDep({
  name: 'polarAnimation',
  type: 'animationRule',
  extension: polarAnimation
});

/**
 * Creates the Polar axis class
 * @class
 */
class Polar extends Axis {
  /**
   * adding animation rules
   */
  constructor () {
    super();
    this.config.scale = new ScaleCategory();
    this.config.referenceInfo = [];
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'polarCategory';
  }

  /**
   * Sets default config
   *
   * @memberof Polar
   */
  __setDefaultConfig () {
    super.__setDefaultConfig();
    this.config.drawPlotBands = false;
    this.config.forceZeroTick = false;
  }

  /**
   * Prepare the axis attributes for use by the chart drawing. It applies default values to the
   * options that are undefined.
   * @param  {Object}  rawAttr The raw attributes to be set
   */
  configureAttributes (rawAttr) {
    let axis = this,
      axisConfig = axis.config,
      axisAttr = rawAttr,
      chart = axis.getFromEnv('chart'),
      jsonData = chart.getFromEnv('dataSource'),
      FCChartObj = jsonData.chart,
      colorM = axis.getFromEnv('color-manager');
    super.configureAttributes(rawAttr);
    axisConfig.gridLineColor = convertColor(pluck(axisAttr.radarSpikeColor,
      colorM.getColor('divLineColor')),
    pluckNumber(axisAttr.radarSpikeAlpha, axisAttr.radarInLineAlpha,
      colorM.getColor('divLineAlpha')));
    axisConfig.radarSpikeThickness = pluckNumber(FCChartObj.radarspikethickness, 1);
    // labelFontBdrColor = labelFontBdrColor ? convertColor(
    //   labelFontBdrColor, pluckNumber(FCChartObj.labelborderalpha,
    //     FCChartObj.labelalpha, 100)) : BLANKSTRING;
    // axisConfig.labels.style = {
    //   fontFamily: pluck(axisAttr.labelFont, axisAttr.outCanfontFamily),
    //   fontSize: pluckNumber(axisAttr.labelFontSize, pInt(axisAttr.outCanfontSize)) + PXSTRING,
    //   fontWeight: pluckNumber(axisAttr.labelFontBold) ? 'bold' :
    //     NORMALSTRING,
    //   fontStyle: pluckNumber(axisAttr.labelFontItalic) ? 'italic' :
    //     NORMALSTRING,
    //   color: convertColor(pluck(axisAttr.labelFontColor, axisAttr.outCancolor),
    //     pluckNumber(axisAttr.labelFontAlpha, 100)),
    //   labelLink: FCChartObj.labellink,
    //   border: labelFontBdrColor || FCChartObj.labelbgcolor ?
    //     (pluckNumber(FCChartObj.labelborderthickness, 1) + 'px solid') : '',
    //   borderColor: labelFontBdrColor,
    //   borderThickness: pluckNumber(FCChartObj.labelborderthickness, 1),
    //   borderPadding: pluckNumber(FCChartObj.labelborderpadding, 2),
    //   borderRadius: pluckNumber(FCChartObj.labelborderradius, 0),
    //   backgroundColor: FCChartObj.labelbgcolor ?
    //     convertColor(FCChartObj.labelbgcolor,
    //       pluckNumber(FCChartObj.labelbgalpha, FCChartObj.labelalpha,
    //         100)) : BLANKSTRING,
    //   borderDash: pluckNumber(FCChartObj.labelborderdashed, 0) ?
    //     getDashStyle(pluckNumber(FCChartObj.labelborderdashlen, 4),
    //       pluckNumber(FCChartObj.labelborderdashgap, 2)) : DASH_DEF
    // };
    // axisConfig.categoryNumDivLines = pluckNumber(axisConfig.numDivLines, 0);
  }

  /**
   * Function set the starting point for drawing the axis and the axis drawing width/height
   * @param {Object} data Contains the x, y, length and whether the axis is drawn on the opposite side
   */
  setAxisDimention (data) {
    let axis = this,
      scale = axis.getScale(),
      axisConfig = axis.config,
      chart = axis.getFromEnv('chart'),
      chartConfig = chart.config,
      startAngle = axisConfig.startAngle || -pi / 2,
      totalAngle = axisConfig.totalAngle || -pi2,
      endAngle = startAngle + totalAngle,
      rangeArr,
      category = axisConfig.tickValues.tickValue || [],
      anglePerSlice = totalAngle / (category.length || 1),
      axisDimention = axisConfig.axisDimention || (axisConfig.axisDimention = {});

    axisDimention.opposite = pluckNumber(data.opposite, axisDimention.opposite);
    axisDimention.centerX = pluckNumber(data.x, axisDimention.x, chartConfig.canvasLeft);
    axisDimention.centerY = pluckNumber(data.y, axisDimention.y, chartConfig.canvasTop);
    axisDimention.axisLength = pi2;

    endAngle -= anglePerSlice;
    rangeArr = axisConfig.clockWise ? [endAngle, startAngle] : [startAngle, endAngle];
    scale.setRange(rangeArr);
  }
  /**
   * Parse category line attributes
   *
   * @memberof Polar
   */
  _parseCategoryPlotLine () {
    var axis = this,
      axisConfig = axis.config,
      tickValue = axisConfig.tickValues.tickValue,
      plotLineColor = axisConfig.gridLineColor,
      plotLineWidth = axisConfig.radarSpikeThickness,
      axisAttrObj,
      referenceInfo = axisConfig.referenceInfo,
      i,
      value;

    axisAttrObj = {
      stroke: plotLineColor,
      'stroke-width': plotLineWidth
      // 'stroke-dasharray': plotLineDashStyle
    };
    // checking for pure tickValue axis exclude bubble scatter
    if (tickValue[0] && (tickValue[0].x !== UNDEF || tickValue[0].y !== UNDEF)) {
      return;
    }
    // checking if lines are to be drawn user can decide
    if (axisConfig.lines.isDraw && axisConfig.numDivLines) {
      for (i = 0; i < tickValue.length; ++i) {
        value = i;

        referenceInfo.push({
          from: value,
          type: 'line',
          layer: 1,
          attr: axisAttrObj
        });
      }
    }
  }
  /**
   * Function called by the chart when there is requirement to calculate or recalculate the space
   * management this function must be called every time when chart dimension is changed
   * @param {number} width The maximum width that is available for space management
   * @param {number} height The maximum height that is available for space management
   * @return {Object} Left and right defining how much space is used in left and right
   */
  placeAxis (width, height) {
    var axis = this,
      axisConfig = axis.config,
      chart = axis.getFromEnv('chart'),
      smartLabel = chart.getFromEnv('smartLabel'),
      labelStyle = axisConfig.labels.style,
      categories = chart.getFromEnv('dataSource').categories,
      axisValuePadding = axisConfig.labelPadding = pluckNumber(axisConfig.rawAttr.axisValuePadding, 10),
      useEllipsesWhenOverflow = axisConfig.useEllipsesWhenOverflow,
      top = 0,
      left = 0,
      right = 0,
      bottom = 0,
      avalTop = height / 2,
      avalBottom = height / 2,
      avalRight = width / 2,
      avalLeft = width / 2,
      i,
      // text,
      // allCategories = axisConfig.categories && axisConfig.categories.category,
      allCategories = axisConfig.tickValues && axisConfig.tickValues.tickValue,
      categoryLength,
      singleCategory,
      smartLabelText,
      categoriesStyle,
      categoryStyle,
      smartLabelStyle,
      smartLabelStyleLineHeight,
      lineHeight;
    // TODO : so space management for category

    axisConfig.labels.isDraw = 1;
    axisConfig.labels.drawNormalVal = 1;
    axisConfig.labels.drawLimitVal = 1;

    if (!axis.components) {
      axis.components = {};
    }
    axis.components.labels = [];
    // The style for the catergories
    if (axisConfig.showLabels) {
      if (categories) {
        let firstCategory = categories[0];
        categoriesStyle = {
          fontFamily: firstCategory.font,
          fontSize: firstCategory.fontsize,
          color: firstCategory.fontcolor
        };
      }
      for (i = 0, categoryLength = allCategories.length; i < categoryLength; i++) {
        singleCategory = allCategories[i];
        // Style for the individuel category
        categoryStyle = {
          fontFamily: singleCategory.font,
          fontSize: singleCategory.fontsize,
          fontWeight: singleCategory.fontbold ? 'bold' : NORMALSTRING,
          fontStyle: singleCategory.fontitalic ? 'italic' : NORMALSTRING
        };
        // the final style for smart label of that individual category label
        smartLabelStyle = {
          fontFamily: pluck(categoryStyle.fontFamily, categoriesStyle.fontFamily,
            labelStyle.fontFamily),
          fontSize: pluck(categoryStyle.fontSize, categoriesStyle.fontSize,
            this.computeFontSize(labelStyle.fontSizeWithUnit) + PXSTRING),
          fontWeight: pluck(categoryStyle.fontWeight, labelStyle.fontWeight),
          fontStyle: pluck(categoryStyle.fontStyle, labelStyle.fontStyle)
        };

        smartLabelStyleLineHeight = setLineHeight(smartLabelStyle);
        lineHeight = smartLabelStyleLineHeight.replace(/px/ig, '');
        smartLabel.useEllipsesOnOverflow(chart.config.useEllipsesWhenOverflow);
        smartLabel.setStyle(smartLabelStyle);

        // Make a copy of actual category label
        singleCategory.oriLabel === UNDEF && (singleCategory.oriLabel = singleCategory.label);
        if (i === 0) {
          smartLabelText = smartLabel.getSmartText(singleCategory.oriLabel, width,
            mathMin(lineHeight, avalTop), useEllipsesWhenOverflow);

          singleCategory.label = smartLabelText.text;
          singleCategory.tooltext = smartLabelText.tooltext;

          smartLabelText.width += smartLabelText.width ? axisValuePadding : 0;
          smartLabelText.height += smartLabelText.height ? axisValuePadding : 0;

          if (smartLabelText.height > top) {
            top = smartLabelText.height;
            bottom = top;
          }
        } else if (i === categoryLength / 2) {
          smartLabelText = smartLabel.getSmartText(singleCategory.oriLabel, width,
            mathMin(lineHeight, avalBottom), useEllipsesWhenOverflow);

          singleCategory.label = smartLabelText.text;
          singleCategory.tooltext = smartLabelText.tooltext;

          smartLabelText.width += smartLabelText.width ? axisValuePadding : 0;
          smartLabelText.height += smartLabelText.height ? axisValuePadding : 0;

          if (smartLabelText.height > bottom) {
            bottom = smartLabelText.height;
          }
        } else if (i < categoryLength / 2) {
          smartLabelText = smartLabel.getSmartText(singleCategory.oriLabel, avalLeft,
            smartLabelStyleLineHeight, useEllipsesWhenOverflow);

          singleCategory.label = smartLabelText.text;
          singleCategory.tooltext = smartLabelText.tooltext;

          smartLabelText.width += smartLabelText.width ? axisValuePadding : 0;
          smartLabelText.height += smartLabelText.height ? axisValuePadding : 0;

          if (smartLabelText.width > left) {
            left = smartLabelText.width;
          }
        } else {
          smartLabelText = smartLabel.getSmartText(singleCategory.oriLabel, avalRight,
            smartLabelStyleLineHeight, useEllipsesWhenOverflow);

          singleCategory.label = smartLabelText.text;
          singleCategory.tooltext = smartLabelText.tooltext;

          smartLabelText.width += smartLabelText.width ? axisValuePadding : 0;
          smartLabelText.height += smartLabelText.height ? axisValuePadding : 0;

          if (smartLabelText.width > right) {
            right = smartLabelText.width;
          }
        }

        // Store width and height of labels
        axis.components.labels.push({
          config: {
            width: smartLabelText.width,
            height: smartLabelText.height,
            props: {
              label: {
                attr: {}
              }
            }
          }
        });
      }
    } else {
      for (i = 0, categoryLength = allCategories.length; i < categoryLength; i++) {
        axis.components.labels.push({
          config: {
            width: 0,
            height: 0,
            props: {
              label: {
                attr: {}
              }
            }
          }
        });
      }
    }
    return {
      top: top,
      left: left,
      right: right,
      bottom: bottom
    };
  }
  /**
   * Calculates the parameters necessary to draw the components of the chart.
   * In addition, it also calculates and draws the axis labels and ticks
   */
  _drawComponents () {
    var axis = this,
      axisConfig = axis.config;
    axisConfig.drawLabels && axisConfig.hasCategory && axis._drawCategory();
  }

  /**
   * Parses axis cosmetics and positions during post space management
   *
   * @memberof Polar
   */
  allocatePosition () {
    var axis = this,
      axisConfig = axis.config;

    axisConfig.drawLabels && axisConfig.hasCategory && axis._parseCategory();
  }
  /**
   * Parses the position and parsing of categories during post space management
   *
   * @memberof Polar
   */
  _parseCategory () {
    var axis = this,
      axisConfig = axis.config,
      chart = axis.getFromEnv('chart'),
      isOpposit = axisConfig.isOpposit,
      labels = axisConfig.labels,
      style = labels.style,
      categoryLabel = axis.config.categoryLabel || (axis.config.categoryLabel = {}),
      categories = chart.getFromEnv('dataSource').categories,
      category = axisConfig.tickValues.tickValue,
      axisPadding = axisConfig.labelPadding,
      categoriesStyle = {},
      categoryStyle = {},
      centerX = axisConfig.axisDimention.centerX,
      centerY = axisConfig.axisDimention.centerY,
      i,
      max,
      min,
      curCategory,
      text,
      labelRotation,
      labelLineHeight,
      addCategoryPadding,
      css,
      axisAttrObj,
      fontColor,
      pos,
      curCategoryValue,
      posX,
      posY,
      radius,
      radiusXY,
      theta,
      axisScale = axis.getScale(),
      catStyle,
      id,
      textId;

    radius = pluckNumber(axisConfig.radius, 0);
    min = 0;
    max = category ? category.length - 1 : 0;

    // checking if labels are to be drawn user can decide or can be decided by axis placeAxis function
    if (axisConfig.labels.isDraw) {
      if (categories) {
        let cat0 = categories[0];
        categoriesStyle.fontFamily = cat0.font;
        categoriesStyle.fontSize = cat0.fontsize;
        categoriesStyle.color = cat0.fontcolor;
      }

      // recalculating font size in case of chart resize
      style.fontSize = this.computeFontSize(style.fontSizeWithUnit);

      // main loop where the drawing starts
      for (i = min; i <= max; i++) {
        curCategory = category[i];
        // TODO : need to
        curCategoryValue = pluckNumber(curCategory.x, curCategory.y, i);
        // if values are not limit value and label drawing is off other
        // than limit increment it and continue
        if (!axisConfig.labels.drawNormalVal && !(axisConfig.labels.drawLimitVal && (i === min ||
                            curCategoryValue === max))) {
          continue;
        }
        if (!axisConfig.labels.drawNormalVal && !(curCategoryValue === min ||
                            curCategoryValue === max)) {
          continue;
        }
        // if value are limit value but limit value are not mean to be drawn
        if (!axisConfig.labels.drawLimitVal && (curCategoryValue === min || curCategoryValue === max)) {
          continue;
        }
        categoryStyle = {
          fontFamily: pluck(curCategory.font, curCategory.labelfont),
          fontSize: pluck(curCategory.fontsize, curCategory.labelfontsize),
          fontWeight: pluckNumber(curCategory.fontbold, curCategory.labelfontbold, 0)
            ? 'bold' : UNDEF,
          fontStyle: pluckNumber(curCategory.fontitalic, curCategory.labelfontitalic, 0)
            ? 'italic' : UNDEF
        };

        fontColor = convertColor(pluck(curCategory.fontcolor, curCategory.labelfontcolor,
          categoriesStyle.color, style.color), pluckNumber(curCategory.labelalpha,
          axisConfig.rawAttr.labelFontAlpha, 100));
        css = {
          fontFamily: pluck(categoryStyle.fontFamily, categoriesStyle.fontFamily, style.fontFamily),
          fontSize: pluck(categoryStyle.fontSize, categoriesStyle.fontSize, style.fontSize),
          fontWeight: pluck(categoryStyle.fontWeight, style.fontWeight),
          fontStyle: pluck(categoryStyle.fontStyle, style.fontStyle)
        };

        css.lineHeight = setLineHeight(css);

        if (css.lineHeight) {
          labelLineHeight = css.lineHeight;
          if (labelLineHeight.indexOf('px') !== -1) {
            labelLineHeight = labelLineHeight.replace(/px/i, '');
            labelLineHeight = parseFloat(labelLineHeight);
          }
        }

        // make the attribute ready to use
        // y-axis
        theta = axisScale.getRangeValue(curCategoryValue);
        pos = polarToCartesian(radius + axisPadding, theta);
        pos.x += centerX;
        pos.y += centerY;
        posX = pos.x;
        posY = pos.y;

        text = curCategory.label || '';
        if (pluckNumber(curCategory.showlabel, 1) === 0 || axisConfig.showLabels === 0) {
          text = '';
        }
        addCategoryPadding = curCategory.labelPadding || 0;
        axisAttrObj = {
          fill: fontColor,
          'line-height': labelLineHeight,
          'font-size': style.fontSize,
          'text': text,
          cursor: curCategory.link ? POINTER : 'default',
          'x': posX,
          'y': posY
        };
        if (addCategoryPadding) {
          axisAttrObj.y += addCategoryPadding;
        }
        if (labelRotation) {
          axisAttrObj['text-anchor'] = isOpposit ? POSITION_START : POSITION_END;
          axisAttrObj['vertical-align'] = POSITION_MIDDLE;
        } else {
          axisAttrObj['vertical-align'] = isOpposit ? POSITION_BOTTOM : POSITION_TOP;
        }

        if (i === 0) {
          axisAttrObj['text-anchor'] = POSITION_MIDDLE;
          axisAttrObj['vertical-align'] = POSITION_BOTTOM;
        } else if (i === (max + 1) / 2) {
          axisAttrObj['text-anchor'] = POSITION_MIDDLE;
          axisAttrObj['vertical-align'] = POSITION_TOP;
        } else if (i < (max + 1) / 2) {
          axisAttrObj['text-anchor'] = POSITION_END;
          axisAttrObj['vertical-align'] = POSITION_MIDDLE;
        } else {
          axisAttrObj['text-anchor'] = POSITION_START;
          axisAttrObj['vertical-align'] = POSITION_MIDDLE;
        }

        theta = axisScale.getRangeValue(curCategoryValue);
        radiusXY = polarToCartesian(radius, theta);
        radiusXY.x += centerX;
        radiusXY.y += centerY;
        // If labels shift is applicable (Specially for 3D) then apply the changes
        if (labels.shiftX) {
          axisAttrObj.x += labels.shiftX;
        }
        if (labels.shiftY) {
          axisAttrObj.y += labels.shiftY;
        }

        // CategoryLabel is not present, create it.
        categoryLabel = axisConfig.categoryLabel = categoryLabel || {};
        // Create/reuse category label
        textId = 'Label' + (id || i);

        categoryLabel[textId] = categoryLabel[textId] || {};
        categoryLabel[textId].config = categoryLabel[textId].config || {};

        categoryLabel[textId].config.labelBox = catStyle = {
          label: text,
          lineHeight: css.lineHeight,
          border: pluckNumber(curCategory.borderthickness, curCategory.labelborderthickness)
            ? (pluckNumber(curCategory.borderthickness, curCategory.labelborderthickness, 1) +
                                'px solid') : '',
          borderColor: curCategory.bordercolor || curCategory.labelbordercolor
            ? convertColor(curCategory.bordercolor || curCategory.labelbordercolor,
              pluckNumber(curCategory.borderalpha, curCategory.labelborderalpha, curCategory.alpha,
                curCategory.labelalpha, 100)) : BLANKSTRING,
          borderThickness: pluckNumber(curCategory.borderthickness, curCategory.labelborderthickness),
          borderPadding: pluckNumber(curCategory.borderpadding, curCategory.labelborderpadding),
          borderRadius: pluckNumber(curCategory.borderradius, curCategory.labelborderradius),
          backgroundColor: curCategory.bgcolor || curCategory.labelbgcolor
            ? convertColor(curCategory.bgcolor || curCategory.labelbgcolor,
              pluckNumber(curCategory.bgalpha, curCategory.labelbgalpha,
                curCategory.alpha, curCategory.labelalpha, 100))
            : BLANKSTRING,
          borderDash: pluckNumber(curCategory.borderdashed, curCategory.labelborderdashed, 0)
            ? getDashStyle(pluckNumber(curCategory.borderdashlen, curCategory.labelborderdashlen, 4),
              pluckNumber(curCategory.borderdashgap, curCategory.labelborderdashgap, 2))
            : pluckNumber(curCategory.borderdashed, curCategory.labelborderdashed) ===
                                0 ? DASH_DEF : style.borderDash
        };
        // Set the categorylabel text-bound cosmetics
        categoryLabel[textId].config.labelBox['text-bound'] = !text
          ? []
          : [pluck(catStyle.backgroundColor, style.backgroundColor),
            pluck(catStyle.borderColor, style.borderColor),
            pluck(catStyle.borderThickness, style.borderThickness),
            pluck(catStyle.borderPadding, style.borderPadding),
            pluck(catStyle.borderRadius, style.borderRadius),
            pluck(catStyle.borderDash, style.borderDash)];

        axisAttrObj['text-bound'] = categoryLabel[textId].config.labelBox['text-bound'];

        axis.components.labels[i].config.props.label.css = css;
        axis.components.labels[i].config.props.label.attr = Object.assign(
          axis.components.labels[i].config.props.label.attr,
          axisAttrObj
        );
      }
    }
  }
  /**
   * Draws the category labels of the axis
   */
  _drawCategory () {
    var axis = this,
      axisConfig = axis.config,
      chart = axis.getFromEnv('chart'),
      animationManager = chart.getFromEnv('animationManager'),
      labels = axisConfig.labels,
      style = labels.style,
      axisContainer = axisConfig.axisContainer,
      category = axisConfig.tickValues.tickValue,
      raiseEvent = function (event) {
        return function (data) {
          var ele = this;
          chart.plotEventHandler(ele, data, event);
        };
      },
      toolTipController = axis.getFromEnv('toolTipController'),
      counter = 0,
      i,
      max,
      min,
      curCategory,
      textElement,
      textElemCheck,
      css,
      axisAttrObj,
      curCategoryValue,
      removeElem,
      id,
      textId,
      mapArr = [],
      removeElementsArr,
      /**
       * Removes element which are required anymore.
       * @param {any} arrOfId array
       */
      removeElements = arrOfId => {
        for (i = 0; i < arrOfId.length; ++i) {
          removeElem = axis.getGraphicalElement(arrOfId[i]);
          removeElem && axis.removeGraphicalElement(removeElem);
        }
      };

    min = 0;
    max = category ? category.length - 1 : 0;

    // checking if labels are to be drawn user can decide or can be decided by axis placeAxis function
    if (axisConfig.labels.isDraw) {
      // main loop where the drawing starts
      for (i = min, counter = 0; i <= max; i++) {
        curCategory = category[i];
        // TODO : need to
        curCategoryValue = pluckNumber(curCategory.x, curCategory.y, i);
        // if values are not limit value and label drawing is off other
        // than limit increment it and continue
        if (!axisConfig.labels.drawNormalVal && !(axisConfig.labels.drawLimitVal && (i === min ||
                            curCategoryValue === max))) {
          continue;
        }
        if (!axisConfig.labels.drawNormalVal && !(curCategoryValue === min ||
                            curCategoryValue === max)) {
          continue;
        }
        // if value are limit value but limit value are not mean to be drawn
        if (!axisConfig.labels.drawLimitVal && (curCategoryValue === min || curCategoryValue === max)) {
          continue;
        }
        textId = 'Label' + (id || i);
        mapArr.push(textId);

        axisAttrObj = axis.components.labels[i].config.props.label.attr;
        css = axis.components.labels[i].config.props.label.css;

        textElemCheck = axis.getGraphicalElement(textId);
        textElement = animationManager.setAnimation({
          css,
          el: textElemCheck || 'text',
          attr: axisAttrObj,
          container: axisContainer,
          component: axis,
          label: 'polarLabel'
        });

        // Attach/remove tooltip
        if (axisConfig.showTooltip && curCategory.tooltext) {
          toolTipController.enableToolTip(textElement, curCategory.tooltext);
        } else {
          toolTipController.disableToolTip(textElement);
        }

        textElement.data(EVENTARGS, {
          link: curCategory.link || curCategory.labellink || style.labelLink,
          text: axisAttrObj.text,
          index: counter
        });
        if (!textElemCheck) {
          axis.addGraphicalElement(textId, textElement);
          // Attach event handler to category label
          textElement.on('fc-click', raiseEvent('datalabelclick'))
            .hover(raiseEvent('dataLabelRollOver'), raiseEvent('dataLabelRollOut'));
        }

        counter += 1;
      }

      axisConfig.labelMap = axisConfig.labelMap || [];
      removeElementsArr = diff(axisConfig.labelMap, mapArr);
    } else {
      removeElementsArr = mapArr;
    }
    axisConfig.labelMap = mapArr;
    removeElements(removeElementsArr);
  }

  /**
   * translation wil be for radar
   * @return {number} value 0
   */
  getTranslation () {
    if (this) return 0;
  }

  /**
   * returns the reference info
   * @return {Array} array of object
  */
  getReferenceInfo () {
    return this.config.referenceInfo;
  }

  /**
   * Sets the visible range of the axis. Also sets axis's reference values and re-calculates PVR.
   * @param  {number} minValue The minimum value of the area to be visible
   * @param  {number} maxValue The maximum value of the area to be visible
   * @return {boolean} Whether the visible range of the axis was successfully set or not
   */
  setVisibleConfig (minValue, maxValue) {
    let axis = this,
      axisConfig = axis.config,
      scale = axis.getScale(),
      domain;

    if (minValue >= maxValue ||
      minValue < axisConfig.axisRange.min ||
      maxValue > axisConfig.axisRange.max) {
      return false;
    }

    domain = scale.setDomain([minValue, maxValue]).getDomain();
    axisConfig.visibleMin = domain[0];
    axisConfig.visibleMax = domain[1];

    axis.clearReferenceInfo();

    axis._parseReferenceVisuals();

    axis.fireEvent('visiblerangeset', { minValue, maxValue });
    axis.asyncDraw();

    return true;
  }

  /**
   * will call api for parsing reference visuals
  */
  _parseReferenceVisuals () {
    this.config.drawPlotlines && this._parseCategoryPlotLine();
  }

  /**
   * will clear the reference visuals
  */
  clearReferenceInfo () {
    let axisConfig = this.config;
    axisConfig.referenceInfo = [];
  }
  _setAxisRange (data) {
    var min = data.min,
      max = data.max,
      axisConfig = this.config,
      axisRange = axisConfig.axisRange,
      divGap = data.divGap,
      step;

    if (min > max) {
      return;
    }

    step = (max - min) / (axisConfig.categoryNumDivLines + 1);

    this.getScale().setDomain([min, max]);
    axisConfig.ticks = range(min, max, step).concat(max);
    axisConfig._allTicks = axisConfig.ticks.slice();
    axisRange.min = min;
    axisRange.max = max;
    axisRange.tickInterval = divGap || step;

    this.setVisibleConfig(axisRange.min, axisRange.max);
  }
}

Polar.prototype.setTickValues = setTickValues;
Polar.prototype.shiftLabels = shiftLabels;
Polar.prototype._createContainer = _createContainer;
Polar.prototype.setAxisPadding = setAxisPadding;
Polar.prototype.getPixel = getPixel;
Polar.prototype.getLabel = getLabel;

export default Polar;
